// src/pages/admin/CompanyManagement.js
import React, { useState, useEffect } from 'react';
import { companyService } from '../../services/apiServices';
import { subscriptionService } from '../../services/subscriptionService';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidator';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import ConfirmationDialog from '../../components/admin/ConfirmationDialog';
import Pagination from '../../components/admin/Pagination';

const CompanyManagement = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    status: 'active',
    // Subscription data
    subscription: {
      status: 'active',
      startDate: new Date().toISOString().split('T')[0], // Today's date
      endDate: '',
      paymentStatus: 'pending',
      aiAccess: 'both',
      aiEnabled: true,
      notes: ''
    },
    // Per-enterprise user quotas ('' = unlimited)
    quotas: {
      maxAdmins: '',
      maxSimpleUsers: '',
      maxViewers: ''
    },
    // Users to be created with the company
    users: []
  });

  // User form for adding users to the company
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'simpleuser'
  });

  const [showUserForm, setShowUserForm] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  // Load data on mount and when filters/page change
  useEffect(() => {
    loadData();
  }, [pagination.page, pagination.limit, searchTerm, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare filters for API - only include defined values
      const filters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) filters.search = searchTerm;
      if (statusFilter !== 'all') filters.status = statusFilter;

      // Load companies and their subscription data
      const [companiesResponse, subscriptionsResponse] = await Promise.allSettled([
        companyService.getCompanies(filters),
        subscriptionService.getSubscriptions()
      ]);

      let companiesData = [];
      let subscriptionsData = [];

      // Handle companies data
      if (companiesResponse.status === 'fulfilled' && companiesResponse.value.success) {
        companiesData = companiesResponse.value.data || [];
        // Update pagination from response if available
        if (companiesResponse.value.pagination) {
          setPagination(prev => ({
            ...prev,
            ...companiesResponse.value.pagination
          }));
        } else {
          // Fallback if API doesn't return pagination yet (though it should)
          setPagination(prev => ({
            ...prev,
            total: companiesData.length,
            pages: Math.ceil(companiesData.length / prev.limit)
          }));
        }
      } else {
        throw new Error('Impossible de charger les données des entreprises');
      }

      // Handle subscriptions data (optional)
      if (subscriptionsResponse.status === 'fulfilled' && subscriptionsResponse.value.success) {
        subscriptionsData = subscriptionsResponse.value.data || [];
      }

      // Normalize and merge data
      const normalizedCompanies = companiesData.map(company => {
        const companyId = company.id || company._id;
        const subscription = subscriptionsData.find(sub =>
          sub.companyId === companyId || sub.companyId === company._id
        );

        return {
          ...company,
          id: companyId,
          subscription: subscription || {
            status: 'inactive',
            startDate: null,
            endDate: null,
            daysRemaining: 0
          }
        };
      });

      setCompanies(normalizedCompanies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    if (field.startsWith('subscription.')) {
      const subscriptionField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          [subscriptionField]: value
        }
      }));
    } else if (field.startsWith('quotas.')) {
      const quotaField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        quotas: {
          ...prev.quotas,
          [quotaField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addUserToCompany = () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.password.trim()) {
      showErrorToast('Tous les champs utilisateur sont requis');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userForm.email)) {
      showErrorToast('Format d\'email invalide');
      return;
    }

    // Check if email already exists in the users list
    if (formData.users.some(user => user.email === userForm.email)) {
      showErrorToast('Un utilisateur avec cet email existe déjà dans la liste');
      return;
    }

    // Validate password complexity
    if (!validatePassword(userForm.password).isValid) {
      showErrorToast('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole');
      return;
    }

    const newUser = {
      id: Date.now(), // Temporary ID for the list
      ...userForm
    };

    setFormData(prev => ({
      ...prev,
      users: [...prev.users, newUser]
    }));

    // Reset user form
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser'
    });
    setShowUserForm(false);
    showSuccessToast('Utilisateur ajouté à la liste');
  };

  const removeUserFromCompany = (userId) => {
    setFormData(prev => ({
      ...prev,
      users: prev.users.filter(user => user.id !== userId)
    }));
    showSuccessToast('Utilisateur retiré de la liste');
  };

  const calculateSubscriptionDays = () => {
    if (!formData.subscription.startDate || !formData.subscription.endDate) return 0;
    const start = new Date(formData.subscription.startDate);
    const end = new Date(formData.subscription.endDate);
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Normalize quotas: '' -> null (unlimited), otherwise integer
      const normalizeQuota = (v) => (v === '' || v === undefined || v === null ? null : parseInt(v, 10));
      const payload = {
        ...formData,
        quotas: {
          maxAdmins: normalizeQuota(formData.quotas?.maxAdmins),
          maxSimpleUsers: normalizeQuota(formData.quotas?.maxSimpleUsers),
          maxViewers: normalizeQuota(formData.quotas?.maxViewers)
        }
      };
      // Client-side quota validation for initial users batch
      if (!isEditing && formData.users.length > 0) {
        const countBy = (r) => formData.users.filter(u => (u.role || 'simpleuser') === r).length;
        if (payload.quotas.maxAdmins !== null && countBy('admin') > payload.quotas.maxAdmins) {
          throw new Error(`Quota administrateurs dépassé (${countBy('admin')}/${payload.quotas.maxAdmins})`);
        }
        if (payload.quotas.maxSimpleUsers !== null && countBy('simpleuser') > payload.quotas.maxSimpleUsers) {
          throw new Error(`Quota analystes dépassé (${countBy('simpleuser')}/${payload.quotas.maxSimpleUsers})`);
        }
        if (payload.quotas.maxViewers !== null && countBy('viewer') > payload.quotas.maxViewers) {
          throw new Error(`Quota lecteurs dépassé (${countBy('viewer')}/${payload.quotas.maxViewers})`);
        }
      }
      let response;
      if (isEditing) {
        response = await companyService.updateCompany(currentCompanyId, payload);
      } else {
        response = await companyService.createCompany(payload);
      }

      if (response.success) {
        const successMessage = isEditing
          ? 'Entreprise mise à jour avec succès!'
          : (response.data.message || 'Entreprise créée avec succès!');

        showSuccessToast(successMessage);

        // Show additional info for created users
        if (!isEditing && response.data.usersCreated && response.data.usersCreated.length > 0) {
          setTimeout(() => {
            showSuccessToast(
              `${response.data.usersCreated.length} utilisateur(s) créé(s) avec l'entreprise`,
              { duration: 4000 }
            );
          }, 1000);
        }

        await loadData();
        resetForm();
      } else {
        throw new Error(response.message || 'Échec de l\'opération');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de ${isEditing ? 'la mise à jour' : 'la création'}: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (company) => {
    setFormData({
      name: company.name,
      domain: company.domain || '',
      status: company.status,
      subscription: {
        status: company.subscription?.status || 'inactive',
        startDate: company.subscription?.startDate ? new Date(company.subscription.startDate).toISOString().split('T')[0] : '',
        endDate: company.subscription?.endDate ? new Date(company.subscription.endDate).toISOString().split('T')[0] : '',
        paymentStatus: company.subscription?.paymentStatus || 'pending',
        aiAccess: company.subscription?.aiAccess || 'both',
        aiEnabled: company.subscription?.aiEnabled ?? true,
        notes: company.subscription?.notes || ''
      },
      quotas: {
        maxAdmins: company.quotas?.maxAdmins ?? '',
        maxSimpleUsers: company.quotas?.maxSimpleUsers ?? '',
        maxViewers: company.quotas?.maxViewers ?? ''
      },
      users: [] // Don't load existing users for editing, just new ones
    });
    setCurrentCompanyId(company.id);
    setIsEditing(true);
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async () => {
    if (!companyToDelete) return;

    setSubmitting(true);
    setShowConfirmation(false);

    try {
      const response = await companyService.deleteCompany(companyToDelete.id);

      if (response.success) {
        showSuccessToast('Entreprise supprimée avec succès!');
        await loadData();
      } else {
        throw new Error(response.message || 'Échec de la suppression');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de la suppression: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
      setCompanyToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      status: 'active',
      subscription: {
        status: 'active',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        paymentStatus: 'pending',
        aiAccess: 'both',
        aiEnabled: true,
        notes: ''
      },
      quotas: {
        maxAdmins: '',
        maxSimpleUsers: '',
        maxViewers: ''
      },
      users: []
    });
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'simpleuser'
    });
    setIsEditing(false);
    setCurrentCompanyId(null);
    setShowForm(false);
    setShowUserForm(false);
    setError(null);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setStatusFilter(value);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getSubscriptionStatusType = (subscription) => {
    if (!subscription || subscription.status !== 'active') return 'warning';
    const daysRemaining = getDaysRemaining(subscription.endDate);
    if (daysRemaining <= 3) return 'danger';
    if (daysRemaining <= 7) return 'warning';
    return 'success';
  };

  // Filter and sort data
  // NOTE: Filtering is now largely handled on the server side via loadData
  const filteredCompanies = [...companies]
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = sortConfig.key.includes('subscription.')
        ? a.subscription[sortConfig.key.split('.')[1]]
        : a[sortConfig.key];
      let bValue = sortConfig.key.includes('subscription.')
        ? b.subscription[sortConfig.key.split('.')[1]]
        : b[sortConfig.key];

      if (sortConfig.key === 'createdAt' || sortConfig.key === 'subscription.endDate') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

  // Calculate metrics
  const metrics = {
    total: pagination.total || companies.length,
    active: companies.filter(c => c.status === 'active').length, // This might be inaccurate if paginated, but dashboard is correct
    inactive: companies.filter(c => c.status === 'inactive').length,
    totalUsers: companies.reduce((sum, c) => sum + (c.userCount || 0), 0),
    activeSubscriptions: companies.filter(c => c.subscription.status === 'active').length
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Entreprise',
      sortable: true,
      render: (value, company) => (
        <div>
          <div className="font-medium text-gray-900 text-sm">{value}</div>
          <div className="text-gray-500 text-xs">{company.domain || 'Aucun domaine'}</div>
        </div>
      )
    },    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value) => (
        <StatusBadge
          status={value === 'active' ? 'Actif' : 'Inactif'}
          type={value === 'active' ? 'success' : 'danger'}
          size="sm"
        />
      )
    },
    {
      key: 'subscription.status',
      label: 'Abonnement',
      sortable: true,
      render: (_, company) => {
        const subscription = company.subscription;
        const daysRemaining = getDaysRemaining(subscription.endDate);

        let statusText = 'Inactif';
        if (subscription.status === 'active') {
          statusText = `Actif (${daysRemaining}j)`;
        } else if (subscription.status === 'expired') {
          statusText = 'Expiré';
        }

        return (
          <StatusBadge
            status={statusText}
            type={getSubscriptionStatusType(subscription)}
            size="sm"
          />
        );
      }
    },
    {
      key: 'userCount',
      label: 'Utilisateurs',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || 0}</span>
    },
    {
      key: 'quotas',
      label: 'Quotas (A/N/L)',
      sortable: false,
      render: (_, company) => {
        const q = company.quotas || {};
        const fmt = (v) => (v === null || v === undefined || v === '' ? '∞' : v);
        return (
          <span className="text-gray-500 text-xs" title="Max admins / analystes / lecteurs">
            {fmt(q.maxAdmins)}/{fmt(q.maxSimpleUsers)}/{fmt(q.maxViewers)}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Date de création',
      sortable: true,
      render: (value) => (
        <span className="text-gray-500 text-sm">
          {value ? new Date(value).toLocaleDateString('fr-FR') : 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, company) => (
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => handleEdit(company)}
            className="text-xs text-blue-600 hover:text-white hover:bg-blue-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={submitting}
          >
            <i className="fas fa-edit text-xs"></i>
            <span>Modifier</span>
          </button>
          <a
            href={`/superadmin/companies/${company.id}/users`}
            className="text-xs text-green-600 hover:text-white hover:bg-green-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
          >
            <i className="fas fa-users text-xs"></i>
            <span>Utilisateurs</span>
          </a>
          <button
            onClick={() => {
              setCompanyToDelete(company);
              setShowConfirmation(true);
            }}
            className="text-xs text-red-600 hover:text-white hover:bg-red-600 font-medium px-2.5 py-1.5 rounded transition-all duration-150 flex items-center gap-1"
            disabled={submitting}
          >
            <i className="fas fa-trash text-xs"></i>
            <span>Supprimer</span>
          </button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Chargement des entreprises...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des Entreprises
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Créez et gérez les entreprises avec leurs abonnements et utilisateurs</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className={`px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-1.5 ${showForm
            ? 'bg-red-500 hover:bg-red-600'
            : 'bg-blue-600 hover:bg-blue-700'
            } focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={submitting}
        >
          <i className={`fas ${showForm ? 'fa-times' : 'fa-plus'} text-xs`}></i>
          <span>{showForm ? 'Annuler' : 'Nouvelle Entreprise'}</span>
        </button>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{metrics.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actives</p>
          <p className="text-2xl font-bold text-green-600 mt-0.5">{metrics.active}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Abonnements</p>
          <p className="text-2xl font-bold text-blue-600 mt-0.5">{metrics.activeSubscriptions}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Utilisateurs</p>
          <p className="text-2xl font-bold text-purple-600 mt-0.5">{metrics.totalUsers}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inactives</p>
          <p className="text-2xl font-bold text-red-600 mt-0.5">{metrics.inactive}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-3">
          <i className="fas fa-exclamation-circle text-red-500"></i>
          <span className="text-sm flex-1">{error}</span>
          <button
            onClick={loadData}
            className="text-sm bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            <i className="fas fa-redo mr-1.5"></i>
            Réessayer
          </button>
        </div>
      )}

      {/* Company Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 relative">
          {submitting && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                <p className="text-sm text-gray-600">Traitement en cours...</p>
              </div>
            </div>
          )}

          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">
              <i className="fas fa-building text-blue-600 mr-2"></i>
              {isEditing ? 'Modifier l\'entreprise' : 'Créer une nouvelle entreprise'}
            </h2>
          </div>

          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 m-5 mb-0">
              <div className="flex items-start">
                <i className="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
                <div>
                  <h3 className="text-sm font-semibold text-blue-800 mb-1">Création complète d'entreprise</h3>
                  <p className="text-sm text-blue-700">
                    Configurez l'entreprise, son abonnement et créez directement ses utilisateurs en une seule fois.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Basic Company Information */}
            <div className="border border-gray-200 rounded-lg bg-gray-50/50">
              <div className="px-4 py-2.5 border-b border-gray-200">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Informations de base</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                    disabled={submitting}
                    placeholder="Entrez le nom de l'entreprise"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Domaine
                  </label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => handleFormChange('domain', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="exemple.com"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="px-4 pb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Statut
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  disabled={submitting}
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>

            {/* Subscription Information */}
            <div className="border border-gray-200 rounded-lg bg-gray-50/50">
              <div className="px-4 py-2.5 border-b border-gray-200">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Configuration de l'abonnement</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Statut de l'abonnement
                  </label>
                  <select
                    value={formData.subscription.status}
                    onChange={(e) => handleFormChange('subscription.status', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                    <option value="expired">Expiré</option>
                    <option value="paused">En pause</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={formData.subscription.startDate}
                    onChange={(e) => handleFormChange('subscription.startDate', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={formData.subscription.endDate}
                    onChange={(e) => handleFormChange('subscription.endDate', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Statut de paiement
                  </label>
                  <select
                    value={formData.subscription.paymentStatus}
                    onChange={(e) => handleFormChange('subscription.paymentStatus', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  >
                    <option value="pending">En attente</option>
                    <option value="paid">Payé</option>
                    <option value="failed">Échec</option>
                    <option value="free">Gratuit</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Droits d'Accès IA
                  </label>
                  <select
                    value={formData.subscription.aiAccess}
                    onChange={(e) => handleFormChange('subscription.aiAccess', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    disabled={submitting}
                  >
                    <option value="both">Local et Externe</option>
                    <option value="local">Local uniquement</option>
                    <option value="external">Externe uniquement</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Activer l'IA
                  </label>
                  <label className="relative inline-flex items-center cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={formData.subscription.aiEnabled}
                      onChange={(e) => handleFormChange('subscription.aiEnabled', e.target.checked)}
                      className="sr-only peer"
                      disabled={submitting}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm text-gray-600">
                      {formData.subscription.aiEnabled ? 'Activé' : 'Désactivé'}
                    </span>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.subscription.notes}
                    onChange={(e) => handleFormChange('subscription.notes', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    rows="3"
                    placeholder="Notes sur l'abonnement..."
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Subscription Summary */}
              {formData.subscription.startDate && formData.subscription.endDate && (
                <div className="mx-4 mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">Durée de l'abonnement</div>
                      <div className="text-2xl font-bold text-gray-900">{calculateSubscriptionDays()} jours</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">Statut</div>
                      <div className="text-lg font-semibold capitalize text-gray-900">{formData.subscription.status}</div>
                    </div>
                  </div>
                  {formData.subscription.notes && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">Notes</div>
                      <div className="text-sm text-gray-700">{formData.subscription.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quotas utilisateurs par entreprise (vide = illimité) */}
            <div className="border border-gray-200 rounded-lg bg-gray-50/50">
              <div className="px-4 py-2.5 border-b border-gray-200">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quotas utilisateurs (vide = illimité)</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Max admins
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quotas.maxAdmins}
                    onChange={(e) => handleFormChange('quotas.maxAdmins', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Illimité"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Max analystes
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quotas.maxSimpleUsers}
                    onChange={(e) => handleFormChange('quotas.maxSimpleUsers', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Illimité"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Max lecteurs
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quotas.maxViewers}
                    onChange={(e) => handleFormChange('quotas.maxViewers', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Illimité"
                    disabled={submitting}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 px-4 pb-4">Laisser vide pour illimité. La baisse sous l'usage actuel bloque seulement les nouvelles créations.</p>
            </div>

            {/* Users Management - Only for new companies */}
            {!isEditing && (
              <div className="border border-gray-200 rounded-lg bg-gray-50/50">
                <div className="px-4 py-2.5 border-b border-gray-200 flex items-center">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Utilisateurs à créer ({formData.users.length})
                  </h3>
                </div>

                <div className="p-4">
                <div className="flex justify-end mb-3">
                  <button
                    type="button"
                    onClick={() => setShowUserForm(!showUserForm)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm transition-colors flex items-center"
                    disabled={submitting}
                  >
                    <i className={`fas ${showUserForm ? 'fa-minus' : 'fa-plus'} mr-2`}></i>
                    {showUserForm ? 'Annuler' : 'Ajouter un utilisateur'}
                  </button>
                </div>

                {/* User Form */}
                {showUserForm && (
                  <div className="bg-white p-4 rounded-md border mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Nouvel utilisateur</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          value={userForm.name}
                          onChange={(e) => handleUserFormChange('name', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="Jean Dupont"
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => handleUserFormChange('email', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="jean@entreprise.com"
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Mot de passe *
                        </label>
                        <input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => handleUserFormChange('password', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          placeholder="••••••••"
                          disabled={submitting}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Rôle
                        </label>
                        <select
                          value={userForm.role}
                          onChange={(e) => handleUserFormChange('role', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                          disabled={submitting}
                        >
                          <option value="simpleuser">Utilisateur simple</option>
                          <option value="admin">Administrateur</option>
                        </select>
                      </div>
                    </div>
                    {userForm.password && (
                      <PasswordRequirements password={userForm.password} />
                    )}
                    <div className="flex justify-end space-x-2 mt-3">
                      <button
                        type="button"
                        onClick={() => setShowUserForm(false)}
                        className="px-3 py-1 text-gray-600 bg-gray-200 rounded text-sm hover:bg-gray-300 transition-colors"
                        disabled={submitting}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={addUserToCompany}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                        disabled={submitting}
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>
                )}

                {/* Users List */}
                {formData.users.length > 0 && (
                  <div className="bg-white rounded-md border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nom
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Rôle
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.users.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-blue-100 text-blue-800'
                                }`}>
                                {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                type="button"
                                onClick={() => removeUserFromCompany(user.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                                disabled={submitting}
                              >
                                <i className="fas fa-trash text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {formData.users.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Aucun utilisateur ajouté. Cliquez sur "Ajouter un utilisateur" pour commencer.
                  </div>
                )}
                </div>
              </div>
            )}

            {/* Form Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={submitting}
              >
                <i className="fas fa-times mr-1.5"></i>
                Annuler
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Traitement...
                  </>
                ) : (
                  <>
                    <i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'} text-xs`}></i>
                    {isEditing ? 'Mettre à jour' : 'Créer l\'entreprise'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search and filters */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'status',
            label: 'Filtrer par statut',
            type: 'select',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous les statuts' },
              { value: 'active', label: 'Actif' },
              { value: 'inactive', label: 'Inactif' }
            ]
          }
        ]}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        placeholder="Rechercher par nom d'entreprise ou domaine..."
      />

      {/* Data table */}
      <DataTable
        data={filteredCompanies}
        columns={columns}
        isLoading={loading}
        onSort={handleSort}
        sortConfig={sortConfig}
        emptyMessage="Aucune entreprise trouvée."
        searchQuery={searchTerm}
        filters={{ status: statusFilter }}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
      />

      {/* Results summary */}
      {filteredCompanies.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-sm text-gray-600">
          Affichage de {filteredCompanies.length} entreprise(s) sur {pagination.total} au total
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setCompanyToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Supprimer l'entreprise"
        message={`Êtes-vous sûr de vouloir supprimer l'entreprise "${companyToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        confirmButtonClass="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        cancelButtonClass="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
      />
    </div>
  );
};

export default CompanyManagement;